import React from 'react';
import { DatePicker, Form, Input, Select, Typography } from 'antd';
import dayjs from 'dayjs';
import type { PreRegistrationForm as PreForm } from './draftForm';
import { METRIC_CATALOGUE } from './results';

interface Props {
  value: PreForm;
  missing: string[];
  onChange: (value: PreForm) => void;
}

// The standard metrics the results page scores; a free-text guardrail is
// allowed but is shown as text, not scored.
const METRIC_OPTIONS = METRIC_CATALOGUE.map((m) => ({
  value: m.key,
  label: `${m.label} (${m.key})`,
}));

const LABEL: Record<string, string> = {
  hypothesis: 'hypothesis',
  primaryMetric: 'primary metric',
  guardrails: 'guardrails',
  minDetectableEffect: 'minimum detectable effect',
  plannedEndDate: 'planned end date',
};

const preFieldLabel = (field: string) => LABEL[field] ?? field;

/** Start stays disabled until every field here is filled (the server checks again). */
export const PreRegistrationForm: React.FC<Props> = ({ value, missing, onChange }) => {
  const set = (patch: Partial<PreForm>) => onChange({ ...value, ...patch });
  return (
    <div data-section="pre-registration">
      {missing.length > 0 && (
        <Typography.Paragraph type="warning">
          Start needs: {missing.map(preFieldLabel).join(', ')}.
        </Typography.Paragraph>
      )}
      <Form.Item label="Hypothesis" required>
        <Input.TextArea
          id="expv2-pre-hypothesis"
          rows={2}
          value={value.hypothesis}
          placeholder="If we give new players 600 coins instead of 500, D1 retention goes up."
          onChange={(e) => set({ hypothesis: e.target.value })}
        />
      </Form.Item>
      <Form.Item label="Primary metric" required>
        <Select
          id="expv2-pre-metric"
          showSearch
          value={value.primaryMetric || undefined}
          options={METRIC_OPTIONS}
          onChange={(primaryMetric: string) => set({ primaryMetric })}
        />
      </Form.Item>
      <Form.Item
        label="Guardrails"
        required
        extra="Metrics that must not get worse. Pick standard metrics to have them scored on the results page."
      >
        <Select
          id="expv2-pre-guardrails"
          mode="tags"
          value={value.guardrails}
          options={METRIC_OPTIONS}
          onChange={(guardrails: string[]) => set({ guardrails })}
        />
      </Form.Item>
      <Form.Item
        label="Minimum detectable effect"
        required
        extra="Smallest change worth detecting, as a fraction (0.02 = 2 points)."
      >
        <Input
          id="expv2-pre-mde"
          value={value.minDetectableEffect}
          onChange={(e) => set({ minDetectableEffect: e.target.value })}
        />
      </Form.Item>
      <Form.Item label="Planned end date" required>
        <DatePicker
          id="expv2-pre-end"
          value={value.plannedEndDate ? dayjs(value.plannedEndDate) : null}
          onChange={(d) => set({ plannedEndDate: d ? d.format('YYYY-MM-DD') : null })}
        />
      </Form.Item>
      <Form.Item label="Custom analysis (optional)">
        <Input.TextArea
          id="expv2-pre-custom"
          rows={2}
          value={value.customAnalysis}
          onChange={(e) => set({ customAnalysis: e.target.value })}
        />
      </Form.Item>
    </div>
  );
};
